import {AGENCY_KEYS} from "@/api/agencyApi";
import {COMPANY_KEYS} from "@/api/companyApi";
import {PAYMENT_KEYS} from "@/api/paymentApi";
import ChevronIcon from "@/assets/colorable/chevron.svg?react";
import TickIcon from "@/assets/colorable/tick.svg?react";
import WarningIcon from "@/assets/colorable/warning.svg?react";
import Button from "@/components/Common/Button";
import CheckBox from "@/components/Common/CheckBox";
import AutosizeTextarea from "@/components/Common/forms/AutosizeTextarea";
import DatePickerField from "@/components/Common/forms/DatePickerField";
import InputField from "@/components/Common/forms/InputField";
import ReusableDropdown, {type TDropdownOption} from "@/components/Common/forms/ReusableDropdown";
import ProInfluencerBadge from "@/components/Common/Influencer/ProInfluencerBadge";
import Loader from "@/components/Common/kpi/Loader";
import TextButton from "@/components/Common/TextButton";
import SkeletonLoader from "@/components/onboarding/SkeletonLoader";
import {useFetchUser} from "@/hooks/useFetchUser";
import usePackages, {getPackageIcon} from "@/hooks/usePackages";
import {useSettingsDirty} from "@/hooks/useSettingsNavigation";
import {client} from "@/httpClient";
import clsxm from "@/lib/clsxm";
import CreditSystemConfig from "@/pages/plans/creditSystem/CreditSystemConfig";
import CurrencySelector from "@/pages/plans/CurrencySelector";
import InfiniteLimitSetter from "@/pages/plans/InfiniteLimitSetter";
import InfluencerFeeConfig from "@/pages/plans/InfluencerFeeConfig";
import TikTokSearchConfig from "@/pages/plans/TikTokSearchConfig";
import {capturePostHogEvent, PostHogEvents} from "@/posthog";
import {queryClient} from "@/queryClient";
import {useSettingsAgencyStore} from "@/store/agency";
import {useSettingsCompanyStore} from "@/store/company";
import {INFINITY_THRESHOLD} from "@/store/constants";
import {settingsModalAtom} from "@/store/jotaiAtoms";
import usePackageAdminStore, {ADMIN_ASSIGNED_TRIAL_DAYS, getSubscriptionCategory} from "@/store/packageAdmin";
import {usePaymentStore} from "@/store/payment";
import type {
  TAgency,
  TCompanyProfile,
  TCurrency,
  TPackageCategory,
  TPackageName,
  TPackagePaymentPeriodType,
} from "@/store/types";
import {useMutation} from "@tanstack/react-query";
import type {AxiosResponse} from "axios";
import dayjs from "dayjs";
import {produce} from "immer";
import {useAtomValue} from "jotai";
import isEqual from "lodash/isEqual";
import omit from "lodash/omit";
import {useEffect, useState, type FC} from "react";
import {useTranslation} from "react-i18next";
import {toast} from "react-toastify";
import {useUnmount, useUpdateEffect} from "react-use";
import {useImmer} from "use-immer";
import {useStateWithDeps} from "use-state-with-deps";

const DISCOUNT_OPTIONS = [0, 5, 10, 15, 20, 25, 30, "custom"] as const;
const YEARLY_DISCOUNT = 10;

const PackageAdmin: FC<{onManageCredits?(type: "add" | "remove"): void}> = ({onManageCredits}) => {
  const {t} = useTranslation(["common", "invoices", "multi_account", "payment_errors", "plans"]);
  const agencyFromStore = useSettingsAgencyStore(state => state.agency);
  const settingsModal = useAtomValue(settingsModalAtom);
  const isAgency = settingsModal?.scope.type === "agency";
  const scrollTo = settingsModal?.scrollTo;
  const companyFromStore = useSettingsCompanyStore(state => state.company);
  const agencyId = isAgency ? agencyFromStore.id : companyFromStore.agency?.id;
  const company = isAgency ? agencyFromStore : companyFromStore;
  const hasSubBrands = agencyFromStore.multiple_brands;
  const currency = isAgency ? company.package_subscription?.currency : company.currency;
  const subscription = company.package_subscription ?? company.future_subscription;

  const companyCurrency = subscription?.currency ?? currency ?? "CHF";
  const [selectedCurrency, setSelectedCurrency] = useStateWithDeps<TCurrency>(companyCurrency as TCurrency, [
    companyCurrency,
  ]);

  const isPackageChanged = usePackageAdminStore(
    state =>
      !isEqual(
        omit(state.modifiedPackage, "subscription_email_sent"),
        omit(state.originalPackage, "subscription_email_sent"),
      ),
  );

  const {
    changeValue,
    changePackage,
    cancelChanges,
    saveChanges,
    customizePackage,
    setInitialPackage,
    originalPackage,
    cancelSubscription,
    applyPackageList,
    changeCurrency,
    reset: resetPackageAdmin,
    setAdminAssignedTrial,
    modifiedPackage: {
      admin_assigned_trial,
      name: selectedPackage,
      price_per_month,
      price_per_year,
      price,
      package_type: paymentPeriod,
      valid_from,
      valid_until,
      influencers_per_campaign,
      invitations_per_campaign,
      extra_campaign_price,
      extra_seat_price,
      number_of_campaigns,
      number_of_seats,
      buyout,
      influencer_level_insights,
      multiple_active_campaigns,
      number_of_multiple_active_campaigns,
      multiple_brands,
      other_countries,
      discount,
      price_per_year_with_discount,
      price_per_month_with_discount,
      template_name,
      subscription_email_sent,
      recurring,
      tiktok_campaigns,
      tiktok_search,
      instagram_campaigns,
      is_admin_assigned,
      campaign_booster,
      access_to_influencer_network,
      content_approval,
      personal_link_stickers,
      pro_creator_mode,
      short_term_campaigns,
      social_feed_filters,
    },
  } = usePackageAdminStore();

  const agencyData = isAgency ? agencyFromStore : companyFromStore.agency;
  const credits = agencyData?.credits ?? 0;
  const proInfluencerFee = agencyData?.pro_service_fee_percentage;
  const regularInfluencerFee = agencyData?.cash_reward_service_fee_percentage;
  const {refetch: refetchUser} = useFetchUser();

  const [influencerFees, setInfluencerFees] = useImmer({
    regular: regularInfluencerFee,
    pro: proInfluencerFee,
  });

  useEffect(() => {
    setInfluencerFees({regular: regularInfluencerFee, pro: proInfluencerFee});
  }, [regularInfluencerFee, proInfluencerFee]);

  const isInfluencerFeeChanged = !isEqual(influencerFees, {regular: regularInfluencerFee, pro: proInfluencerFee});

  const agencyDisableIntercom = useSettingsAgencyStore(state => state.agency.disable_intercom);
  const companyDisableIntercom = companyFromStore.agency?.disable_intercom;
  const disableIntercom = (isAgency ? agencyDisableIntercom : companyDisableIntercom) ?? false;
  const [disableIntercomDraft, setDisableIntercomDraft] = useStateWithDeps(disableIntercom, [disableIntercom]);
  const isDisableIntercomChanged = disableIntercomDraft !== disableIntercom;

  const agencyDisableGenerativeAi = useSettingsAgencyStore(state => state.agency.disable_generative_ai);
  const companyDisableGenerativeAi = companyFromStore.agency?.disable_generative_ai;
  const disableGenerativeAi = (isAgency ? agencyDisableGenerativeAi : companyDisableGenerativeAi) ?? false;
  const [disableGenerativeAiDraft, setDisableGenerativeAiDraft] = useStateWithDeps(disableGenerativeAi, [
    disableGenerativeAi,
  ]);
  const isDisableGenerativeAiChanged = disableGenerativeAiDraft !== disableGenerativeAi;

  const agencyAllowContentApprovalWithoutMinCash = useSettingsAgencyStore(
    state => state.agency.allow_content_approval_without_min_cash,
  );
  const companyAllowContentApprovalWithoutMinCash = companyFromStore.agency?.allow_content_approval_without_min_cash;
  const allowContentApprovalWithoutMinCash =
    (isAgency ? agencyAllowContentApprovalWithoutMinCash : companyAllowContentApprovalWithoutMinCash) ?? false;
  const [allowContentApprovalWithoutMinCashDraft, setAllowContentApprovalWithoutMinCashDraft] = useStateWithDeps(
    allowContentApprovalWithoutMinCash,
    [allowContentApprovalWithoutMinCash],
  );
  const isAllowContentApprovalWithoutMinCashChanged =
    allowContentApprovalWithoutMinCashDraft !== allowContentApprovalWithoutMinCash;

  const agencyIncludeInfluencerEmail = useSettingsAgencyStore(
    state => state.agency.include_influencer_email_in_address_download,
  );
  const companyIncludeInfluencerEmail = companyFromStore.agency?.include_influencer_email_in_address_download;
  const includeInfluencerEmail = (isAgency ? agencyIncludeInfluencerEmail : companyIncludeInfluencerEmail) ?? false;
  const [includeInfluencerEmailDraft, setIncludeInfluencerEmailDraft] = useStateWithDeps(includeInfluencerEmail, [
    includeInfluencerEmail,
  ]);
  const isIncludeInfluencerEmailChanged = includeInfluencerEmailDraft !== includeInfluencerEmail;

  useSettingsDirty(
    isPackageChanged ||
      isInfluencerFeeChanged ||
      isDisableIntercomChanged ||
      isDisableGenerativeAiChanged ||
      isAllowContentApprovalWithoutMinCashChanged ||
      isIncludeInfluencerEmailChanged,
  );

  const getPackageCategory = (): TPackageCategory => {
    if (tiktok_campaigns && instagram_campaigns) return "BOTH";
    if (tiktok_campaigns) return "TIKTOK";
    return "INSTAGRAM";
  };
  const packageCategory = getPackageCategory();

  const {extendedPackagesList: packagesList, packagesData, isPending} = usePackages(selectedCurrency, packageCategory);

  const packageDiscountedPrice =
    paymentPeriod === "YEARLY" ? price_per_year_with_discount! : price_per_month_with_discount!;

  const originalPrice = paymentPeriod === "YEARLY" ? price_per_year! : price_per_month!;

  const getCustomDiscountPercentage = () => {
    const discountOtherThanPackageDiscount = Math.round(
      ((discount! - (originalPrice - packageDiscountedPrice)) / (packageDiscountedPrice || 1)) * 100,
    );
    return Math.round(discountOtherThanPackageDiscount);
  };
  const customDiscountPercentage = getCustomDiscountPercentage();

  const [isCustomDiscount, setIsCustomDiscount] = useState(
    !DISCOUNT_OPTIONS.some(selectableValue => selectableValue === customDiscountPercentage),
  );

  const isEditingAlreadyExistingSubscription = originalPackage.name !== "NONE";
  const isCustomPackage = !!template_name;
  const showMultiAccountFields = subscription?.multiple_brands || (isAgency && hasSubBrands && isCustomPackage);
  const isTrial = !!admin_assigned_trial;
  const isConvertingTrial = !!originalPackage.admin_assigned_trial && !isTrial;
  // A trial converts as a list plan (no template_name), so the initial-period price input
  // must not depend on the custom-plan identity.
  const isConvertingToInitialPeriodPlan =
    isConvertingTrial &&
    (paymentPeriod === "QUARTERLY" || paymentPeriod === "HALF_YEARLY" || paymentPeriod === "THREE_PLUS_TWELVE");
  const getPlans = usePaymentStore(state => state.getPlans);
  const subscriptionId = subscription?.id;

  useEffect(() => {
    if (isPending) return;
    setInitialPackage(getPlans(getSubscriptionCategory(subscription ?? null)), subscription ?? null);
  }, [isPending, subscriptionId]);

  useUpdateEffect(() => {
    if (packagesData[0]?.currency === selectedCurrency) changeCurrency(packagesData);
  }, [packagesData]);

  useUnmount(resetPackageAdmin);

  const togglePlatform = (platform: "instagram_campaigns" | "tiktok_campaigns", enabled: boolean) => {
    changeValue(platform, enabled);
    applyPackageList(
      getPlans(
        getSubscriptionCategory({
          instagram_campaigns: platform === "instagram_campaigns" ? enabled : instagram_campaigns,
          tiktok_campaigns: platform === "tiktok_campaigns" ? enabled : tiktok_campaigns,
        }),
      ),
    );
  };

  const {mutateAsync: savePackageChanges, isPending: isSaveChangesLoading} = useMutation({
    meta: {
      errorMessage:
        paymentPeriod !== "MONTHLY" && paymentPeriod !== "YEARLY" && !price && !isTrial
          ? t("payment_errors:package_price_zero_error")
          : isAgency
            ? extra_campaign_price === 0
              ? t("payment_errors:extra_campaign_price_zero_error")
              : extra_seat_price === 0
                ? t("payment_errors:extra_seat_price_zero_error")
                : void 0
            : void 0,
      skipSuccessToast: true,
    },
    mutationKey: PAYMENT_KEYS.mPackageSubscriptionList,
    mutationFn: () => saveChanges(Number(agencyId), isAgency, selectedCurrency, subscription ?? null),

    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: isAgency ? AGENCY_KEYS.qAgency(agencyId) : COMPANY_KEYS.qCompany(company.id),
      }),
  });

  const {mutateAsync: saveAgencyChanges, isPending: isAgencyChangesLoading} = useMutation({
    meta: {skipSuccessToast: true},
    mutationKey: AGENCY_KEYS.mAgency(agencyId!),
    mutationFn: (payload: {
      allow_content_approval_without_min_cash?: boolean;
      cash_reward_service_fee_percentage?: number | null;
      disable_generative_ai?: boolean;
      disable_intercom?: boolean;
      include_influencer_email_in_address_download?: boolean;
      pro_service_fee_percentage?: number | null;
    }) => client.patch(AGENCY_KEYS.mAgency(agencyId!)[0], payload),
    onSuccess: (_, payload) => {
      if (isAgency) {
        queryClient.setQueryData<AxiosResponse<TAgency>>(
          AGENCY_KEYS.qAgency(agencyId),
          prevState =>
            prevState &&
            produce(prevState, draft => {
              Object.assign(draft.data, payload);
            }),
        );
      } else {
        queryClient.setQueryData<AxiosResponse<TCompanyProfile>>(
          COMPANY_KEYS.qCompany(company.id),
          prevState =>
            prevState &&
            produce(prevState, draft => {
              Object.assign(draft.data.agency, payload);
            }),
        );
      }
    },
  });

  const [cancellationReason, setCancellationReason] = useStateWithDeps("", [agencyId, subscription?.id]);
  const [cancelNow, setCancelNow] = useStateWithDeps(false, [is_admin_assigned]);
  const {mutate: cancelSub, isPending: isCancelSubLoading} = useMutation({
    meta: {skipSuccessToast: true},
    mutationKey: PAYMENT_KEYS.mPackageSubscriptionCancel,
    mutationFn: async () => {
      await cancelSubscription(Number(agencyId), cancelNow, cancellationReason);

      capturePostHogEvent(PostHogEvents.SubscriptionCancelled, {
        cancelNow,
        companyId: company.id,
        packageName: selectedPackage,
      });

      toast.success(
        cancelNow
          ? t("subscription_cancelled_now")
          : t("subscription_cancelled_later", {
              date: dayjs(valid_until).format("DD.MM.YYYY"),
            }),
      );
    },
    onSuccess: () => {
      setCancellationReason("");
      return queryClient.invalidateQueries({
        queryKey: isAgency ? AGENCY_KEYS.qAgency(agencyId) : COMPANY_KEYS.qCompany(company.id),
      });
    },
  });

  const {mutate: sendEmail, isPending: isLoadingEmail} = useMutation({
    mutationKey: PAYMENT_KEYS.mPackageEmailSubscribe,
    mutationFn: () => client.post(PAYMENT_KEYS.mPackageEmailSubscribe[0], {agency_id: agencyId}),
    onMutate: () => {
      changeValue("subscription_email_sent", true);
    },
  });

  const isEssentialsPlan = originalPackage.name === "ESSENTIALS";

  const getPackageOptions = () => {
    const all = packagesList.map(packageOption => ({
      value: packageOption.type,
      label: t(packageOption.translationKey as "packageAdvanced"),
    }));
    return all.filter(option => {
      if (option.value === "STARTER") return false;
      if (!isEssentialsPlan && option.value === "ESSENTIALS") return false;
      if (option.value === "ENTERPRISE" && !isEditingAlreadyExistingSubscription && !(isAgency && isCustomPackage))
        return false;
      return true;
    });
  };
  const packageOptions = getPackageOptions();
  const selectedPackageOption = packageOptions.find(option => option.value === selectedPackage);

  const paymentPeriodOptions: {
    value: TPackagePaymentPeriodType;
    label: string;
    discountLabel: string;
    discountPercent: number;
  }[] = [
    {
      value: "MONTHLY",
      label: t("monthly"),
      discountLabel: t("costPerMonth"),
      discountPercent: 100 * (1 - (price_per_month_with_discount ?? 0) / (price_per_month || 1)),
    },
    {
      value: "YEARLY",
      label: t("yearly"),
      discountLabel: t("costPerYear"),
      discountPercent: 100 * (1 - (price_per_year_with_discount ?? 0) / (price_per_year || 1)),
    },
    {
      value: "QUARTERLY",
      label: t("quarterly"),
      discountLabel: t("costPerQuarter"),
      discountPercent: 0,
    },
    {
      value: "HALF_YEARLY",
      label: t("halfYearly"),
      discountLabel: t("costPerHalfYear"),
      discountPercent: 0,
    },
    {
      value: "THREE_PLUS_TWELVE",
      label: t("threePlusTwelve"),
      discountLabel: t("costPerQuarter"),
      discountPercent: 0,
    },
  ];

  const selectedPaymentPeriodOption = paymentPeriodOptions.find(option => option.value === paymentPeriod);

  const discountOptions: TDropdownOption[] = DISCOUNT_OPTIONS.map(discountOption => ({
    value: discountOption.toString(),
    label: discountOption === "custom" ? t("custom") : discountOption.toString() + "%",
  }));

  const selectedDiscountOption =
    discountOptions.find(option => +option.value === customDiscountPercentage && !isCustomDiscount) ??
    discountOptions.at(-1);

  useEffect(() => {
    setIsCustomDiscount(selectedDiscountOption?.value === "custom");
  }, [selectedDiscountOption?.value]);

  const getDiscounts = (): {[x: string]: {percentage: number; text: string}} => {
    const packageDiscount = selectedPaymentPeriodOption
      ? Math.round(
          100 *
            (1 -
              (selectedPaymentPeriodOption.value === "YEARLY"
                ? (price_per_year_with_discount ?? 0) / (price_per_year || 1)
                : (price_per_month_with_discount ?? 0) / (price_per_month || 1))),
        )
      : 0;
    const paymentPeriodDiscount =
      selectedPaymentPeriodOption?.value === "YEARLY" && !isCustomPackage ? YEARLY_DISCOUNT : 0;
    const overall = Math.round(
      customDiscountPercentage * (packageDiscountedPrice / (originalPrice || 1)) + packageDiscount,
    );
    return {
      overall: {
        percentage: isCustomPackage ? 0 : overall,
        text: t("discount") + ` ${isCustomPackage ? 0 : overall}%`,
      },
      custom: {
        percentage: customDiscountPercentage,
        text: `(${t("custom")} ${customDiscountPercentage}%${
          packageDiscountedPrice / originalPrice < 1 ? " - " + t("onlyAppliedAfterPackageDiscount") : ""
        })`,
      },
      package: {
        percentage: packageDiscount,
        text: `(${t("packageDiscount")} ${Math.abs(Math.round(packageDiscount))}%)`,
      },
      paymentPeriod: {
        percentage: paymentPeriodDiscount,
        text: `(${selectedPaymentPeriodOption?.label} ${Math.abs(Math.round(paymentPeriodDiscount))}%)`,
      },
    };
  };
  const discounts = getDiscounts();

  if (isPending) return <SkeletonLoader className="h-64 rounded-lg" />;

  return (
    <div className="flex flex-col gap-5">
      <div className="border-neutral-grey-lightest bg-neutral-grey-lightest shadow-elevation-sm flex flex-col gap-10 rounded-lg border p-5">
        <div className="flex flex-col gap-5">
          <div className="flex w-full justify-between">
            <span className="text-neutral-black text-sm font-medium">
              {t(
                !company.package_subscription && !!company.future_subscription
                  ? "accounts_future_package"
                  : "accounts_current_package",
              ) +
                (!recurring
                  ? ` (${t("validUntil", {
                      date: dayjs(valid_until).format("DD.MM.YYYY"),
                    })})`
                  : ` (${t("recurring")})`)}
            </span>
            {selectedPackage !== "ENTERPRISE" &&
              selectedPackage !== "NONE" &&
              !isTrial &&
              !isEditingAlreadyExistingSubscription && (
                <TextButton
                  text={t("customize")}
                  onClick={() => customizePackage(isAgency)}
                  className="text-sm font-semibold"
                />
              )}
            {isEditingAlreadyExistingSubscription &&
              (isCancelSubLoading ? (
                <Loader styles={{containerClassName: "size-5"}} />
              ) : (
                <TextButton
                  text={t("cancelSubscription")}
                  onClick={() => cancelSub()}
                  className="text-sm font-semibold"
                />
              ))}
          </div>
          {(subscription?.stripe_subscription_url ||
            subscription?.assigned_from_payment_link ||
            subscription?.stripe_customer_url) && (
            <div className="flex flex-col gap-3">
              {subscription?.assigned_from_payment_link && (
                <div className="text-warning-medium-yellow bg-warning-light-yellow flex items-center gap-2 rounded-md px-3 py-2">
                  <WarningIcon className="size-4 shrink-0" />
                  <span className="text-sm font-semibold">{t("package_purchased_via_payment_link")}</span>
                </div>
              )}
              <div className="flex flex-col gap-1.5">
                {subscription.stripe_customer_url && (
                  <TextButton
                    text={t("view_customer_in_stripe")}
                    onClick={() => window.open(subscription.stripe_customer_url!, "_blank")}
                    className="w-fit text-sm font-semibold"
                  />
                )}
                {subscription?.stripe_subscription_url && (
                  <TextButton
                    text={t("view_subscription_in_stripe")}
                    onClick={() => window.open(subscription.stripe_subscription_url!, "_blank")}
                    className="w-fit text-sm font-semibold"
                  />
                )}
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-5">
          {isTrial && (
            <div className="bg-warning-light-yellow flex flex-wrap items-center justify-between gap-x-4 gap-y-2 rounded-md px-3 py-2">
              <div className="text-warning-dark-yellow flex items-center gap-2">
                <WarningIcon className="size-4 shrink-0" aria-hidden />
                <span className="text-sm font-semibold">
                  {t("plans:free_trial_banner", {currency: selectedCurrency, days: ADMIN_ASSIGNED_TRIAL_DAYS})}
                </span>
              </div>
              <TextButton
                text={t(isEditingAlreadyExistingSubscription ? "plans:convert_to_paid_plan" : "plans:leave_free_trial")}
                onClick={() => setAdminAssignedTrial(false, packagesData ?? [])}
                className="text-sm font-semibold"
              />
            </div>
          )}
          {isEditingAlreadyExistingSubscription && selectedPackage === originalPackage.name && (
            <>
              <CheckBox
                isChecked={cancelNow}
                onChange={setCancelNow}
                className="size-5 shrink-0"
                label={t("check_to_cancel_plan_now")}
              />
              <AutosizeTextarea
                disabled={isCancelSubLoading}
                htmlFor="admin-cancellation-reason"
                id="admin-cancellation-reason"
                minRows={3}
                onChange={event => setCancellationReason(event.target.value)}
                optional
                placeholder={t("admin_cancellation_reason_placeholder")}
                title={t("admin_cancellation_reason_label")}
                value={cancellationReason}
              />
              <div className="bg-neutral-grey-light h-px w-full" />
            </>
          )}
          <ReusableDropdown
            setSelectedOption={option =>
              changePackage(option.value as TPackageName, packagesData ?? [], subscription ?? null)
            }
            label={t("clients_package")}
            selectedOption={selectedPackageOption}
            isDisabled={isEditingAlreadyExistingSubscription}
            options={packageOptions}
            renderButton={({selectedOption, isOpen, className, ...rest}) => (
              <button
                {...rest}
                data-testid="package-admin-package-dropdown"
                className={clsxm(
                  className,
                  isOpen ? "border-primary-medium-solid" : "border-neutral-grey-light",
                  selectedOption ? "text-neutral-black font-medium" : "text-neutral-grey-light",
                  "h-10 gap-2 rounded-md pl-4",
                )}>
                {selectedOption &&
                  getPackageIcon({
                    packageType: selectedOption.value as TPackageName,
                    defaultIcon: true,
                    className: "size-3.5",
                  })}
                <span className="text-neutral-black grow items-center text-left text-xs">
                  {selectedOption?.label ?? t("clients_package")}
                </span>
                <ChevronIcon
                  className={clsxm(
                    "mr-4 transition-transform duration-250",
                    isOpen ? "rotate-180" : "rotate-0",
                    isEditingAlreadyExistingSubscription ? "text-neutral-grey-dark" : "text-primary-dark-solid",
                  )}
                />
              </button>
            )}
            renderItem={({option, isHighlighted, isSelected, className, key, ...rest}) => {
              return (
                <li
                  key={key}
                  {...rest}
                  data-testid={`package-admin-package-option-${option.value.toLowerCase()}`}
                  value={option.value}
                  className={clsxm(
                    className,
                    isHighlighted ? "bg-primary-light-solid" : "hover:bg-neutral-white-dark",
                    "h-10 gap-2",
                  )}>
                  {getPackageIcon({
                    packageType: option.value as TPackageName,
                    defaultIcon: true,
                    className: "size-3.5",
                  })}
                  <span className="grow text-xs leading-none">{option.label}</span>
                  {isSelected && <TickIcon className="text-primary-dark-solid" />}
                </li>
              );
            }}
            labelClassName="text-neutral-black text-xs font-normal"
          />
          {selectedPackage !== "NONE" && (
            <>
              <div className="flex flex-wrap items-center gap-5 sm:flex-nowrap">
                <ReusableDropdown
                  label={t("paymentPeriod")}
                  setSelectedOption={option => {
                    changeValue("package_type", option.value as TPackagePaymentPeriodType);

                    if (
                      !isCustomPackage &&
                      !isTrial &&
                      !isConvertingTrial &&
                      (option.value === "QUARTERLY" ||
                        option.value === "HALF_YEARLY" ||
                        option.value === "THREE_PLUS_TWELVE")
                    ) {
                      customizePackage(isAgency);
                    }
                  }}
                  isDisabled={isTrial || (isEditingAlreadyExistingSubscription && !isConvertingTrial)}
                  selectedOption={selectedPaymentPeriodOption}
                  options={paymentPeriodOptions}
                  renderButton={({selectedOption, isOpen, className, ...rest}) => (
                    <button
                      {...rest}
                      className={clsxm(
                        className,
                        isOpen ? "border-primary-medium-solid" : "border-neutral-grey-light",
                        selectedOption ? "text-neutral-black font-medium" : "text-neutral-grey-light",
                        "h-10 gap-2 rounded-md pl-4",
                      )}>
                      <span className="text-neutral-black grow items-center text-left font-medium">
                        {selectedOption?.label ?? t("selectPaymentPeriod")}
                      </span>
                      <ChevronIcon
                        className={clsxm(
                          "text-primary-dark-solid mr-4 transition-transform duration-250",
                          isOpen ? "rotate-180" : "rotate-0",
                          isTrial || (isEditingAlreadyExistingSubscription && !isConvertingTrial)
                            ? "text-neutral-grey-dark"
                            : "text-primary-dark-solid",
                        )}
                      />
                    </button>
                  )}
                  renderItem={({option, isHighlighted, isSelected, className, ...rest}) => {
                    return (
                      <li
                        {...rest}
                        value={option.value}
                        className={clsxm(
                          className,
                          isHighlighted ? "bg-primary-light-solid" : "hover:bg-neutral-white-dark",
                          "h-10 gap-2",
                        )}>
                        <span className="grow leading-none">{option.label}</span>
                        {isSelected && <TickIcon className="text-primary-dark-solid" />}
                      </li>
                    );
                  }}
                  labelClassName="text-neutral-black text-xs font-normal"
                />
                <div className="sm:mt-5">
                  <CheckBox
                    isChecked={recurring}
                    disabled={isTrial || (isEditingAlreadyExistingSubscription && !isConvertingTrial)}
                    onChange={newValue => changeValue("recurring", newValue)}
                    className="size-4.5 shrink-0"
                    label={t("recurring")}
                    labelClassName="whitespace-nowrap"
                  />
                </div>
              </div>
              <div className="flex items-center">
                <DatePickerField
                  isDisabled={isTrial}
                  minDate={dayjs().subtract(20, "years").toDate()}
                  maxDate={dayjs(valid_until).toDate()}
                  iconClassName="max-md:size-3.5"
                  inputFieldClassName="flex-1 h-10 max-md:text-xs"
                  labelClassName="text-neutral-black mb-0 text-xs font-normal"
                  onChange={valid_from => changeValue("valid_from", dayjs(valid_from).toISOString())}
                  value={dayjs(valid_from).toDate()}
                  label={t("start_date")}
                  placeholder={t("start_date")}
                />
                <div className="w-4" />
                <DatePickerField
                  isDisabled={isTrial}
                  minDate={dayjs(valid_from).toDate()}
                  iconClassName="max-md:size-3.5"
                  inputFieldClassName="flex-1 h-10 max-md:text-xs"
                  labelClassName="text-neutral-black mb-0 text-xs font-normal"
                  onChange={valid_until => changeValue("valid_until", dayjs(valid_until).toISOString())}
                  value={dayjs(valid_until).toDate()}
                  label={t("end_date")}
                  placeholder={t("end_date")}
                />
              </div>
              <div className="flex gap-5">
                <ReusableDropdown
                  containerClassName={isCustomDiscount ? "w-full" : ""}
                  setSelectedOption={option => {
                    setIsCustomDiscount(option?.value === "custom");
                    if (option?.value !== "custom") {
                      const customDiscountPercentage = +option.value;

                      changeValue(
                        "discount",
                        Math.round(
                          originalPrice -
                            packageDiscountedPrice +
                            (packageDiscountedPrice * customDiscountPercentage) / 100,
                        ),
                      );
                    }
                  }}
                  isDisabled={isTrial}
                  label={t("discount")}
                  selectedOption={selectedDiscountOption}
                  options={discountOptions}
                  renderButton={({selectedOption, isOpen, className, ...rest}) => (
                    <button
                      {...rest}
                      className={clsxm(
                        className,
                        isOpen ? "border-primary-medium-solid" : "border-neutral-grey-light",
                        selectedOption ? "text-neutral-black font-medium" : "text-neutral-grey-light",
                        "h-10 gap-2 rounded-md pl-4",
                      )}>
                      <span className="grow items-center text-left">
                        {selectedOption?.label ?? t("select_discount")}
                      </span>
                      <ChevronIcon
                        className={clsxm(
                          "text-primary-dark-solid mr-4 transition-transform duration-250",
                          isOpen ? "rotate-180" : "rotate-0",
                        )}
                      />
                    </button>
                  )}
                  renderItem={({option, isHighlighted, isSelected, className, ...rest}) => {
                    return (
                      <li
                        {...rest}
                        value={option.value}
                        className={clsxm(
                          className,
                          isHighlighted ? "bg-primary-light-solid" : "hover:bg-neutral-white-dark",
                          "h-10 gap-2",
                        )}>
                        <span className="grow leading-none">{option.label}</span>
                        {isSelected && <TickIcon className="text-primary-dark-solid" />}
                      </li>
                    );
                  }}
                  labelClassName="text-neutral-black text-xs font-normal"
                />
                {selectedDiscountOption?.value === "custom" && (
                  <InputField
                    className="h-10 flex-1"
                    isDisabled={isTrial}
                    labelClassName="text-neutral-black mb-0 text-xs font-normal"
                    label={t("customDiscountPercentage")}
                    type="number"
                    placeholder={t("customDiscountPercentage")}
                    onChange={value => {
                      const customDiscountPercentage = +value;
                      if (customDiscountPercentage >= 0 && customDiscountPercentage <= 100)
                        changeValue(
                          "discount",
                          Math.round(
                            originalPrice -
                              packageDiscountedPrice +
                              (packageDiscountedPrice * customDiscountPercentage) / 100,
                          ),
                        );
                    }}
                    value={customDiscountPercentage.toString()}
                  />
                )}
              </div>
              {(isCustomPackage || showMultiAccountFields || isConvertingToInitialPeriodPlan) && (
                <InputField
                  label={
                    paymentPeriod === "THREE_PLUS_TWELVE" ? t("price_first_three_months") : t("price_before_discounts")
                  }
                  type="number"
                  placeholder={t("price")}
                  className="h-10"
                  isDisabled={isTrial}
                  labelClassName="text-neutral-black mb-0 text-xs font-normal"
                  onChange={value => {
                    if (+value >= 0) changeValue("price", +value);
                  }}
                  value={price?.toString()}
                />
              )}
              {paymentPeriod === "THREE_PLUS_TWELVE" && (
                <InputField
                  label={t("yearly_renewal_price")}
                  type="number"
                  placeholder={t("price")}
                  className="h-10"
                  isDisabled={isTrial}
                  labelClassName="text-neutral-black mb-0 text-xs font-normal"
                  onChange={value => {
                    if (+value >= 0) changeValue("price_per_year", +value);
                  }}
                  value={price_per_year?.toString()}
                />
              )}
              <div className="flex flex-col gap-2">
                <span className="text-neutral-black text-xs font-semibold">{t("finalPrice")}</span>
                <div className="flex items-center gap-2">
                  <span
                    className={clsxm(
                      "text-lg font-semibold",
                      !isTrial &&
                        (!!discounts.overall.percentage ||
                          !!discounts.custom.percentage ||
                          !!discounts.paymentPeriod.percentage) &&
                        "line-through",
                    )}>
                    {selectedPaymentPeriodOption?.value === "YEARLY" && !isCustomPackage
                      ? Math.round(price! / ((100 - YEARLY_DISCOUNT) / 100))
                      : price}
                  </span>
                  {!isTrial &&
                    (!!discounts.overall.percentage ||
                      !!discounts.custom.percentage ||
                      !!discounts.paymentPeriod.percentage) && (
                      <span className="text-neutral-grey-dark text-xs">
                        {Object.values(discounts)
                          .filter(discount => discount.percentage !== 0)
                          .map(discount => discount.text)
                          .join(" ")}
                      </span>
                    )}
                </div>
                <div className="flex flex-wrap items-center justify-between gap-5">
                  <div className="flex items-end gap-2">
                    <CurrencySelector
                      selectedCurrency={selectedCurrency}
                      setCurrency={option => setSelectedCurrency(option.value as TCurrency)}
                      isPending={isPending}
                    />
                    <InputField
                      type="number"
                      isDisabled
                      value={Math.round(
                        price! *
                          (1 - (isCustomPackage ? discounts.custom.percentage : discounts.overall.percentage) / 100),
                      ).toString()}
                      className="text-3xl font-semibold"
                      containerClassName="max-w-52"
                    />
                    <span className="bg-linear-(--brand-medium-gradient) bg-clip-text text-transparent">
                      {selectedPaymentPeriodOption?.discountLabel}
                    </span>
                  </div>
                  <div className="ml-auto flex flex-wrap items-center gap-2">
                    {!isEditingAlreadyExistingSubscription &&
                      !isTrial &&
                      !(selectedPackage === "ENTERPRISE" && !isCustomPackage) && (
                        <Button
                          onClick={() => setAdminAssignedTrial(true, packagesData ?? [])}
                          buttonStyle="White"
                          className="h-10 w-fit px-4 text-sm">
                          {t("plans:free_trial")}
                        </Button>
                      )}
                    <Button
                      isDisabled={originalPackage.name === "NONE" || subscription_email_sent}
                      onClick={() => sendEmail()}
                      isLoading={isLoadingEmail}
                      buttonStyle="Blue"
                      className="h-10 w-fit px-4 text-sm">
                      {t("informBrand")}
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
          {(isCustomPackage || showMultiAccountFields) && (
            <>
              <div className="bg-neutral-grey-light h-px w-full" />
              <div className="flex flex-wrap gap-5 lg:flex-nowrap">
                <InputField
                  label={t("influencerPerCampaign")}
                  type="number"
                  className="h-10 flex-1"
                  labelClassName="text-neutral-black mb-0 text-xs font-normal"
                  containerClassName="justify-between"
                  placeholder="#"
                  onChange={value => {
                    if (+value >= 0) changeValue("influencers_per_campaign", +value);
                  }}
                  value={
                    typeof influencers_per_campaign !== "number" || influencers_per_campaign >= INFINITY_THRESHOLD
                      ? ""
                      : influencers_per_campaign.toString()
                  }
                  icon={<InfiniteLimitSetter storeKey="influencers_per_campaign" />}
                />
                <InputField
                  label={t("influencerInvitations")}
                  type="number"
                  className="h-10 flex-1"
                  labelClassName="text-neutral-black mb-0 text-xs font-normal"
                  containerClassName="justify-between"
                  placeholder="#"
                  onChange={value => {
                    if (+value >= 0) changeValue("invitations_per_campaign", +value);
                  }}
                  value={
                    typeof invitations_per_campaign !== "number" || invitations_per_campaign >= INFINITY_THRESHOLD
                      ? ""
                      : invitations_per_campaign.toString()
                  }
                  icon={<InfiniteLimitSetter storeKey="invitations_per_campaign" />}
                />
                <InputField
                  label={t("invoices:content_buyouts")}
                  type="number"
                  className="h-10 flex-1"
                  labelClassName="text-neutral-black mb-0 text-xs font-normal"
                  containerClassName="justify-between"
                  placeholder="#"
                  onChange={value => {
                    if (+value >= 0) changeValue("buyout", +value);
                  }}
                  value={typeof buyout !== "number" || buyout >= INFINITY_THRESHOLD ? "" : buyout.toString()}
                  icon={<InfiniteLimitSetter storeKey="buyout" />}
                />
              </div>
              {showMultiAccountFields && (
                <div className="grid grid-cols-2 gap-5">
                  <div className="col-span-2 flex items-center">
                    <CheckBox
                      isChecked={multiple_brands}
                      onChange={newValue => changeValue("multiple_brands", newValue)}
                      className="size-4.5 shrink-0"
                      label={t("multi_account:multiple_brands")}
                    />
                  </div>
                  <InputField
                    label={t("multi_account:amount_of_campaigns")}
                    type="number"
                    className="h-10 flex-1"
                    labelClassName="text-neutral-black mb-0 text-xs font-normal"
                    containerClassName="justify-between"
                    placeholder="#"
                    onChange={value => {
                      if (+value >= 0) changeValue("number_of_campaigns", +value);
                    }}
                    value={
                      typeof number_of_campaigns !== "number" || number_of_campaigns >= INFINITY_THRESHOLD
                        ? ""
                        : number_of_campaigns.toString()
                    }
                    icon={<InfiniteLimitSetter storeKey="number_of_campaigns" />}
                  />
                  <InputField
                    label={t("multi_account:price_per_additional_campaign_currency", {currency})}
                    type="number"
                    className="h-10 flex-1"
                    labelClassName="text-neutral-black mb-0 text-xs font-normal"
                    containerClassName="justify-between"
                    placeholder="#"
                    onChange={value => {
                      if (+value >= 0) changeValue("extra_campaign_price", +value);
                    }}
                    value={typeof extra_campaign_price !== "number" ? "" : extra_campaign_price.toString()}
                  />
                  <InputField
                    label={t("multi_account:amount_of_seats")}
                    type="number"
                    className="h-10 flex-1"
                    labelClassName="text-neutral-black mb-0 text-xs font-normal"
                    containerClassName="justify-between"
                    placeholder="#"
                    onChange={value => {
                      if (+value >= 0) changeValue("number_of_seats", +value);
                    }}
                    value={
                      typeof number_of_seats !== "number" || number_of_seats >= INFINITY_THRESHOLD
                        ? ""
                        : number_of_seats.toString()
                    }
                    icon={<InfiniteLimitSetter storeKey="number_of_seats" />}
                  />
                  <InputField
                    label={t("multi_account:price_per_additional_seat_currency", {
                      currency,
                    })}
                    type="number"
                    className="h-10 flex-1"
                    labelClassName="text-neutral-black mb-0 text-xs font-normal"
                    containerClassName="justify-between"
                    placeholder="#"
                    onChange={value => {
                      if (+value >= 0) changeValue("extra_seat_price", +value);
                    }}
                    value={typeof extra_seat_price !== "number" ? "" : extra_seat_price.toString()}
                  />
                </div>
              )}
            </>
          )}
          {selectedPackage !== "NONE" && (
            <>
              <div className="bg-neutral-grey-light h-px w-full" />
              <div className="flex flex-col gap-3">
                <span className="text-neutral-black text-xs">{t("social_access")}</span>
                <div className="flex flex-wrap items-center gap-5">
                  <CheckBox
                    disabled={!tiktok_campaigns}
                    isChecked={instagram_campaigns}
                    onChange={newValue => togglePlatform("instagram_campaigns", newValue)}
                    className="size-4.5 shrink-0"
                    label={t("instagramEnabled")}
                  />
                  <CheckBox
                    disabled={!instagram_campaigns}
                    isChecked={tiktok_campaigns}
                    onChange={newValue => togglePlatform("tiktok_campaigns", newValue)}
                    className="size-4.5 shrink-0"
                    label={t("tiktokEnabled")}
                  />
                </div>
              </div>
              <div className="bg-neutral-grey-light h-px w-full" />
              <div className="flex flex-col gap-3">
                <span className="text-neutral-black text-xs">{t("extras")}</span>
                <div className="flex flex-wrap items-center gap-4">
                  <CheckBox
                    isChecked={other_countries}
                    onChange={newValue => changeValue("other_countries", newValue)}
                    className="size-4.5 shrink-0"
                    label={t("dachRegion")}
                  />
                  <CheckBox
                    isChecked={influencer_level_insights}
                    onChange={newValue => changeValue("influencer_level_insights", newValue)}
                    className="size-4.5 shrink-0"
                    label={t("campaignAnalytics")}
                  />
                  <CheckBox
                    isChecked={multiple_active_campaigns}
                    onChange={newValue => changeValue("multiple_active_campaigns", newValue)}
                    className="size-4.5 shrink-0"
                    label={t("multipleCampaigs")}
                  />
                  <CheckBox
                    isChecked={access_to_influencer_network}
                    onChange={newValue => changeValue("access_to_influencer_network", newValue)}
                    className="size-4.5 shrink-0"
                    label={t("access_to_influencer_network")}
                  />
                  <CheckBox
                    isChecked={campaign_booster}
                    onChange={newValue => changeValue("campaign_booster", newValue)}
                    className="size-4.5 shrink-0"
                    label={t("campaign_booster")}
                  />
                  <div className="flex items-center gap-2">
                    <CheckBox
                      isChecked={pro_creator_mode}
                      onChange={newValue => changeValue("pro_creator_mode", newValue)}
                      className="size-4.5 shrink-0"
                      label={t("pro_creator_mode")}
                    />
                    <ProInfluencerBadge labelled />
                  </div>
                  <CheckBox
                    isChecked={content_approval}
                    onChange={newValue => changeValue("content_approval", newValue)}
                    className="size-4.5 shrink-0"
                    label={t("content_approval")}
                  />
                  <CheckBox
                    isChecked={social_feed_filters}
                    onChange={newValue => changeValue("social_feed_filters", newValue)}
                    className="size-4.5 shrink-0"
                    label={t("social_feed_filters")}
                  />
                  <CheckBox
                    isChecked={personal_link_stickers}
                    onChange={newValue => changeValue("personal_link_stickers", newValue)}
                    className="size-4.5 shrink-0"
                    label={t("personal_link_stickers")}
                  />
                  <CheckBox
                    isChecked={tiktok_search}
                    onChange={newValue => changeValue("tiktok_search", newValue)}
                    className="size-4.5 shrink-0"
                    label={t("tiktok_search")}
                  />
                  <CheckBox
                    isChecked={short_term_campaigns}
                    onChange={newValue => changeValue("short_term_campaigns", newValue)}
                    className="size-4.5 shrink-0"
                    label={t("short_term_campaigns")}
                  />
                  <CheckBox
                    isChecked={disableIntercomDraft}
                    onChange={setDisableIntercomDraft}
                    className="size-4.5 shrink-0"
                    label={t("plans:disable_intercom")}
                  />
                  <CheckBox
                    isChecked={disableGenerativeAiDraft}
                    onChange={setDisableGenerativeAiDraft}
                    className="size-4.5 shrink-0"
                    label={t("plans:disable_generative_ai")}
                  />
                  <CheckBox
                    isChecked={allowContentApprovalWithoutMinCashDraft}
                    onChange={setAllowContentApprovalWithoutMinCashDraft}
                    className="size-4.5 shrink-0"
                    label={t("plans:allow_content_approval_without_min_cash")}
                  />
                  <CheckBox
                    isChecked={includeInfluencerEmailDraft}
                    onChange={setIncludeInfluencerEmailDraft}
                    className="size-4.5 shrink-0"
                    label={t("plans:include_influencer_email_in_address_download")}
                  />
                </div>
                {multiple_active_campaigns && (
                  <InputField
                    label={t("plans:number_of_multiple_active_campaigns_limit")}
                    type="number"
                    className="h-10"
                    labelClassName="text-neutral-black mb-0 text-xs font-normal"
                    containerClassName="max-w-64 justify-between"
                    placeholder="#"
                    icon={
                      <InfiniteLimitSetter
                        storeKey="number_of_multiple_active_campaigns"
                        infiniteValue={null}
                        isAlwaysVisible
                      />
                    }
                    onChange={value => {
                      if (+value >= 0) changeValue("number_of_multiple_active_campaigns", +value);
                    }}
                    value={
                      typeof number_of_multiple_active_campaigns === "number"
                        ? number_of_multiple_active_campaigns.toString()
                        : ""
                    }
                  />
                )}
                {(isCustomPackage || showMultiAccountFields) && (tiktok_search || (isAgency && hasSubBrands)) && (
                  <TikTokSearchConfig agencyId={agencyId} hasSubBrands={hasSubBrands} isAgency={isAgency} />
                )}
              </div>
              <div className="bg-neutral-grey-light h-px w-full" />
            </>
          )}

          {selectedPackage !== "NONE" && (
            <>
              <InfluencerFeeConfig influencerFees={influencerFees} setInfluencerFees={setInfluencerFees} />
              <div className="bg-neutral-grey-light h-px w-full" />
            </>
          )}
        </div>

        <div className="flex justify-end gap-2.5 lg:gap-5">
          {selectedPackage === "NONE" ? (
            <div className="flex-1" />
          ) : (
            <Button
              isDisabled={
                !isPackageChanged &&
                !isInfluencerFeeChanged &&
                !isDisableIntercomChanged &&
                !isDisableGenerativeAiChanged &&
                !isAllowContentApprovalWithoutMinCashChanged &&
                !isIncludeInfluencerEmailChanged
              }
              onClick={() => {
                if (isPackageChanged) {
                  cancelChanges();
                }

                if (isInfluencerFeeChanged) {
                  setInfluencerFees({regular: regularInfluencerFee, pro: proInfluencerFee});
                }

                if (isDisableIntercomChanged) {
                  setDisableIntercomDraft(disableIntercom);
                }

                if (isDisableGenerativeAiChanged) {
                  setDisableGenerativeAiDraft(disableGenerativeAi);
                }

                if (isAllowContentApprovalWithoutMinCashChanged) {
                  setAllowContentApprovalWithoutMinCashDraft(allowContentApprovalWithoutMinCash);
                }

                if (isIncludeInfluencerEmailChanged) {
                  setIncludeInfluencerEmailDraft(includeInfluencerEmail);
                }
              }}
              buttonStyle="White"
              className="h-10 self-end rounded-lg px-5 text-sm">
              {t("cancel")}
            </Button>
          )}
          <Button
            isDisabled={
              (!isPackageChanged &&
                !isInfluencerFeeChanged &&
                !isDisableIntercomChanged &&
                !isDisableGenerativeAiChanged &&
                !isAllowContentApprovalWithoutMinCashChanged &&
                !isIncludeInfluencerEmailChanged) ||
              (!template_name && isCustomPackage)
            }
            onClick={async () => {
              if (isPackageChanged) {
                await savePackageChanges();
              }
              if (
                isInfluencerFeeChanged ||
                isDisableIntercomChanged ||
                isDisableGenerativeAiChanged ||
                isAllowContentApprovalWithoutMinCashChanged ||
                isIncludeInfluencerEmailChanged
              ) {
                await saveAgencyChanges({
                  ...(isInfluencerFeeChanged && {
                    cash_reward_service_fee_percentage: influencerFees.regular,
                    pro_service_fee_percentage: influencerFees.pro,
                  }),
                  ...(isDisableGenerativeAiChanged && {disable_generative_ai: disableGenerativeAiDraft}),
                  ...(isDisableIntercomChanged && {disable_intercom: disableIntercomDraft}),
                  ...(isAllowContentApprovalWithoutMinCashChanged && {
                    allow_content_approval_without_min_cash: allowContentApprovalWithoutMinCashDraft,
                  }),
                  ...(isIncludeInfluencerEmailChanged && {
                    include_influencer_email_in_address_download: includeInfluencerEmailDraft,
                  }),
                });
              }
              if (isDisableIntercomChanged || isDisableGenerativeAiChanged) {
                await refetchUser();
              }
              toast.success(t("toast_global_success"));
            }}
            isLoading={isSaveChangesLoading || isAgencyChangesLoading}
            buttonStyle="Gradient"
            dataTestId="package-admin-save-button"
            className="h-10 flex-1 rounded-lg px-5 text-sm">
            {t("saveChanges")}
          </Button>
        </div>
      </div>

      {(is_admin_assigned || !!credits) && (
        <div
          id="credit-system-config"
          ref={node => {
            if (node && scrollTo === "credits") node.scrollIntoView({behavior: "smooth", block: "end"});
          }}>
          <CreditSystemConfig
            className="border-neutral-grey-lightest bg-neutral-grey-lightest shadow-elevation-sm rounded-lg border p-5"
            onManageCredits={onManageCredits}
            value={credits}
          />
        </div>
      )}
    </div>
  );
};

export default PackageAdmin;
